import { useEffect, useState } from 'react'
import type { CSSProperties } from 'react'
import { providerClient, watchTime } from '../provider/providerClient'
import type { TitleDetail as Detail } from '../provider/providerClient'

/**
 * ★★ THE TWO LINKED CHARTS — day-by-day, and the hours of whichever day you click (v1's shape,
 * built after David overruled deferring them).
 *
 * ⚠⚠ IT IS NOT A HEATMAP, and that distinction is the whole design: two charts, one feeding the
 * other, so the day chart answers "when did people watch this" and the hour chart answers "at
 * what time of day" for a chosen day — or across the whole period until one is chosen.
 *
 * ★ EVERY DAY IN THE PERIOD IS DRAWN, including the empty ones. Watching is sparse — seven days
 * of activity inside a year, on live — and drawing only the days with data would put seven bars
 * side by side as if they were consecutive. **The gaps are the shape**, and a chart that hides
 * them tells a supplier something untrue about their own audience.
 *
 * ⚠ THE AXIS SCALES TO THE DATA, always. v1 fixed its maximum at 40 views / 3000 seconds, clipped
 * anything larger under `overflow: hidden`, and then grew a manual −/+ zoom control to work
 * around its own scale. Both are symptoms of the same mistake.
 */

const panel: CSSProperties = {
  background: 'var(--card)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius)',
  padding: '14px 16px',
  marginBottom: 12,
}
const head: CSSProperties = {
  fontSize: 'var(--fs-xs)',
  color: 'var(--muted-foreground)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  marginBottom: 10,
}
const muted: CSSProperties = { color: 'var(--muted-foreground)', fontSize: 'var(--fs-sm)' }
const tiny: CSSProperties = { color: 'var(--muted-foreground)', fontSize: 'var(--fs-xs)' }

function label(day: number): string {
  const s = String(day)
  return s.length === 8 ? `${s.slice(6, 8)}/${s.slice(4, 6)}/${s.slice(0, 4)}` : String(day)
}

export function TitleDetailPanel({
  contentKey,
  period,
  onClose,
}: {
  contentKey: string
  period: string
  onClose: () => void
}) {
  const [detail, setDetail] = useState<Detail | null>(null)
  const [day, setDay] = useState<number | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    setDetail(null)
    setDay(null)
    providerClient
      .title(contentKey, period)
      .then((d) => {
        if (!cancelled) setDetail(d)
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Could not load this title.')
      })
    return () => {
      cancelled = true
    }
  }, [contentKey, period])

  if (error) return <div style={panel}><span style={{ ...muted, color: 'var(--destructive)' }}>{error}</span></div>
  if (!detail) return <div style={panel}><span style={muted}>Loading…</span></div>
  if (!detail.Found) return <div style={panel}><span style={muted}>That title is not in your catalogue.</span></div>

  const days = detail.Days ?? []
  const maxDay = Math.max(1, ...days.map((d) => d.WatchSeconds))
  const chosen = day === null ? null : days.find((d) => d.Day === day) ?? null

  // The hour chart shows the chosen day, or the whole period until one is chosen.
  const hours = chosen
    ? chosen.Hours.map((h) => ({ Hour: h.Hour, WatchSeconds: h.WatchSeconds }))
    : (detail.Hours ?? []).map((h) => ({ Hour: h.Hour, WatchSeconds: h.WatchSeconds }))
  const maxHour = Math.max(1, ...hours.map((h) => h.WatchSeconds))
  const active = days.filter((d) => d.WatchSeconds > 0).length

  return (
    <div style={{ ...panel, borderColor: 'var(--primary)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline', marginBottom: 10 }}>
        <div>
          <strong>{detail.Title}</strong>{' '}
          <span style={tiny}>
            {detail.IsRollUp ? '· including everything beneath it' : `· ${detail.EntryType}`}
          </span>
        </div>
        <button
          type="button"
          className="admin-btn"
          style={{
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius)',
            padding: '3px 10px',
            fontSize: 'var(--fs-sm)',
            cursor: 'pointer',
            color: 'var(--foreground)',
          }}
          onClick={onClose}
        >
          Close
        </button>
      </div>

      <div style={head}>
        Watch time by day · {detail.Period?.Label} · {active} {active === 1 ? 'day' : 'days'} with watching
      </div>
      {/* ⚠ One column per day of the period, empty ones included. A year is 365 thin bars; that
          IS the information — a supplier should see how spread out their audience is. */}
      {/*
        ⚠ THE STRIP MUST FIT ITS CARD. A minimum bar width plus a gap, times a year of days,
        overflowed the panel and drew the bars OUTSIDE it — 365 × (2px + 1px gap) is wider than
        the page. Long periods drop the gap and let flex share the width; `overflow: hidden` is
        the backstop so nothing can escape the card again.
      */}
      {/*
        ⚠ ON A PHONE THE STRIP SCROLLS RATHER THAN SQUEEZING. Sharing 380px between a quarter's 90
        days gives a 4px column — visible, but nothing you can tap, and tapping is what drives the
        hour chart below. So below the tablet breakpoint the columns take a real minimum width and
        the strip scrolls inside its own box; the page itself never scrolls sideways. Bucketing to
        weeks would be the obvious alternative and it is the wrong one — the empty days are the
        shape, and a week bucket hides exactly the gaps this chart exists to show.
        ⚠ `flex` and `min-width` live in layout.css, NOT here: inline styles beat classes, so a
        width written here would silently defeat the breakpoint.
      */}
      <div className="dash-daystrip" style={{ height: 96, marginBottom: 6 }}>
        <div className="dash-daystrip-inner" style={{ height: '100%', gap: days.length > 120 ? 0 : 1 }}>
        {days.map((d) => {
          const isChosen = d.Day === day
          return (
            <button
              key={d.Day}
              type="button"
              title={`${label(d.Day)} · ${watchTime(d.WatchSeconds)}`}
              aria-label={`${label(d.Day)}: ${watchTime(d.WatchSeconds)}`}
              onClick={() => setDay(isChosen ? null : d.Day)}
              className="dash-day"
              style={{
                height: `${Math.max(d.WatchSeconds > 0 ? 4 : 1, (d.WatchSeconds / maxDay) * 100)}%`,
                // ★ The chosen day is highlighted rather than merely outlined — it is driving the
                // chart below, so it has to be obvious which one is.
                background: isChosen ? 'var(--warning)' : d.WatchSeconds > 0 ? 'var(--primary)' : 'var(--border)',
                border: 'none',
                borderRadius: 1,
                padding: 0,
                cursor: d.WatchSeconds > 0 ? 'pointer' : 'default',
              }}
            />
          )
        })}
        </div>
      </div>
      <div style={{ ...tiny, display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
        <span>{days.length > 0 ? label(days[0].Day) : ''}</span>
        <span>{chosen ? `Showing ${label(chosen.Day)} below — click again to clear` : 'Click a day to see its hours'}</span>
        <span>{days.length > 0 ? label(days[days.length - 1].Day) : ''}</span>
      </div>

      <div style={head}>{chosen ? `Hours of ${label(chosen.Day)}` : 'Hours across the whole period'} (UTC)</div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 72 }}>
        {hours.map((h) => (
          <div
            key={h.Hour}
            title={`${String(h.Hour).padStart(2, '0')}:00 · ${watchTime(h.WatchSeconds)}`}
            style={{
              flex: '1 1 0',
              height: `${Math.max(h.WatchSeconds > 0 ? 4 : 1, (h.WatchSeconds / maxHour) * 100)}%`,
              background: h.WatchSeconds > 0 ? 'var(--primary)' : 'var(--border)',
              borderRadius: 2,
            }}
          />
        ))}
      </div>
      <div style={{ ...tiny, display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
        <span>00:00</span>
        <span>12:00</span>
        <span>23:00</span>
      </div>
      {/* ⚠ Said once, plainly: the clock is UTC because that is what the platform records, and a
          supplier reading "3am" should know it is not their local evening. */}
      <div style={{ ...tiny, marginTop: 8 }}>
        Times are UTC, and watch time is measured in 3-second samples.
      </div>
    </div>
  )
}
